import { SALES_TEAM_POSITIONS } from '@/data/sales-team-positions';
import { ENGINEERING_READ_SCOPES } from '@/utils/charts/engineering';

const chartLink = (id, title, icon, scope, description) => ({
    id,
    title,
    icon,
    to: `/charts/${id}`,
    scope,
    description,
});

export const CHARTS_MENU_TO = '/charts';

export const CHART_CURRENCIES = [
    { code: 'MXN', name: 'MXN' },
    { code: 'USD', name: 'USD' },
];

export const CHART_GROUPS = [
    {
        id: 'sales',
        title: 'Sales',
        icon: 'ri-money-dollar-box-line',
        items: [
            {
                id: 'sales-overview',
                title: 'Sales overview',
                icon: 'ri-dashboard-3-line',
                to: '/charts/sales-overview',
                scope: 'read_charts_customers',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Company-wide acquisition, retention, upsell, and goal pace at a glance.',
            },
            {
                id: 'total-sales',
                title: 'Total sales',
                icon: 'ri-line-chart-line',
                to: '/charts/total-sales',
                scope: 'read_charts_total_sales',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Overall sales performance.',
            },
            {
                id: 'salespeople',
                title: 'Salespeople',
                icon: 'ri-user-line',
                to: '/charts/salespeople',
                scope: 'read_charts_salespeople',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'How agents on the sales team are performing.',
            },
            {
                id: 'partners',
                title: 'Partners',
                icon: 'ri-shake-hands-line',
                to: '/charts/partners',
                scope: 'read_charts_partners',
                description: 'How partners are contributing to sales.',
            },
            {
                id: 'accounts',
                title: 'Accounts',
                icon: 'ri-store-2-line',
                to: '/charts/accounts',
                scope: 'read_charts_accounts',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Sales, fees, and profit by account.',
            },
        ],
    },
    {
        id: 'finance',
        title: 'Finance',
        icon: 'ri-wallet-line',
        items: [
            chartLink(
                'customer-liability',
                'Owed to customers',
                'ri-wallet-line',
                'read_charts_customer_liability',
                'Cash still owed to merchant wallets, including withdrawals that have not been paid.',
            ),
            chartLink(
                'chargebacks',
                'Chargebacks',
                'ri-error-warning-line',
                'read_charts_chargebacks',
                'Open disputes, chargeback rate vs last period, and merchants to watch.',
            ),
            chartLink(
                'refunds',
                'Refunds',
                'ri-refund-2-line',
                'read_charts_refunds',
                'Refund rate versus payments, whether it is improving, and new accounts refunding too much.',
            ),
            chartLink(
                'withdrawals',
                'Withdrawals',
                'ri-exchange-dollar-line',
                'read_charts_withdrawals',
                'Withdrawal volume by currency, compared with the previous period, and payouts that need review.',
            ),
        ],
    },
    {
        id: 'customers',
        title: 'Customers',
        icon: 'ri-team-line',
        items: [
            {
                id: 'customers',
                title: 'Customers',
                icon: 'ri-team-line',
                to: '/charts/customers',
                scope: 'read_charts_customers',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Who is new, growing, or churning, and how their volume changed versus the previous period.',
            },
            {
                id: 'customers-churning',
                title: 'Churning',
                icon: 'ri-user-unfollow-line',
                to: '/charts/customers-churning',
                scope: 'read_charts_customers',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Customers whose volume so far today is below their usual same weekday.',
            },
            {
                id: 'customers-acquisition',
                title: 'Acquisition',
                icon: 'ri-user-add-line',
                to: '/charts/customers-acquisition',
                scope: 'read_charts_customers',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'New customers by first payment: volume, fees, profit, location, and how they compare with the previous period.',
            },
            {
                id: 'customers-activation',
                title: 'Activation',
                icon: 'ri-user-follow-line',
                to: '/charts/customers-activation',
                scope: 'read_charts_customers',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Of the customers created each month, how many made their first transaction in the same month and in later months.',
            },
        ],
    },
    {
        id: 'pricing',
        title: 'Pricing',
        icon: 'ri-price-tag-3-line',
        items: [
            {
                id: 'pricing',
                title: 'Pricing',
                icon: 'ri-price-tag-3-line',
                to: '/charts/pricing',
                scope: 'read_charts_pricing',
                description: 'Prices recently set on customers, what changed, and the estimated profit impact.',
            },
            {
                id: 'affiliations',
                title: 'Affiliations',
                icon: 'ri-bank-card-line',
                to: '/charts/affiliations',
                scope: 'read_charts_pricing',
                description: 'Processed volume by affiliation MCC family, and which customers have an affiliation set or pending.',
            },
        ],
    },
    {
        id: 'marketing',
        title: 'Marketing',
        icon: 'ri-megaphone-line',
        items: [
            {
                id: 'marketing',
                title: 'Marketing',
                icon: 'ri-megaphone-line',
                to: '/charts/marketing',
                scope: 'read_charts_marketing',
                description: 'Signups and activations by acquisition source.',
            },
            {
                id: 'marketing-map',
                title: 'Map',
                icon: 'ri-map-pin-line',
                to: '/charts/marketing-map',
                scope: 'read_charts_marketing',
                description: 'Where signups, customers, and first payers sit by account address, by quantity or amount.',
            },
        ],
    },
    {
        id: 'product',
        title: 'Product',
        icon: 'ri-box-3-line',
        items: [
            {
                id: 'product-usage',
                title: 'Product usage',
                icon: 'ri-bar-chart-2-line',
                to: '/charts/product-usage',
                scope: 'read_charts_product_usage',
                description: 'How merchants are using each product.',
            },
        ],
    },
    {
        id: 'engineering',
        title: 'Engineering',
        icon: 'ri-git-pull-request-line',
        items: [
            chartLink(
                'engineering',
                'Engineering',
                'ri-git-pull-request-line',
                ENGINEERING_READ_SCOPES,
                'Pull requests released, time to release, fixes that followed each feature, and code reviews per developer.',
            ),
        ],
    },
    {
        id: 'risk',
        title: 'Risk',
        icon: 'ri-shield-check-line',
        items: [
            {
                id: 'acceptance-rate',
                title: 'Acceptance rate',
                icon: 'ri-percent-line',
                to: '/charts/acceptance-rate',
                scope: 'read_charts_acceptance_rate',
                bypassPositions: SALES_TEAM_POSITIONS,
                description: 'Approved vs declined card attempts.',
            },
        ],
    },
];

export const flattenChartItems = (groups) =>
    groups.flatMap((group) =>
        group.items.map((item) => ({ ...item, groupTitle: group.title, groupId: group.id })),
    );

export const isChartsView = (path) =>
    typeof path === 'string' && (path === CHARTS_MENU_TO || path.startsWith(`${CHARTS_MENU_TO}/`));

export const findChartByPath = (path) => {
    for (const group of CHART_GROUPS) {
        const item = group.items.find(
            (entry) => entry.to && (path === entry.to || path.startsWith(`${entry.to}/`)),
        );
        if (item) {
            return { group, item };
        }
    }
    return null;
};
